import { useState, useEffect, useRef } from 'react'
import { useMsal } from '@azure/msal-react'
import { ArrowRight, Info, Lock, AlertCircle, AlertTriangle, RefreshCw } from 'lucide-react'
import { loginRequest } from '../../authConfig'

// ── Medallion SVG ────────────────────────────────────────────────────────────

function Medallion({ size = 32, onDark = false }: { size?: number; onDark?: boolean }) {
  const brand = onDark ? 'oklch(0.85 0.07 198)' : 'var(--ns-brand)'
  const fill  = onDark ? 'none' : 'var(--ns-text)'
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" style={{ display: 'block', flexShrink: 0 }}>
      <rect width="64" height="64" rx={Math.round(size * 0.22)} fill={fill} />
      <path d="M20 40 L44 40 L40 48 L24 48 Z" fill={brand} fillOpacity="0.55" />
      <path d="M19 28 L45 28 L41 36 L23 36 Z" fill={brand} fillOpacity="0.8" />
      <path d="M18 16 L46 16 L42 24 L22 24 Z" fill={brand} />
    </svg>
  )
}

// ── Microsoft Logo ────────────────────────────────────────────────────────────

function MicrosoftLogo() {
  return (
    <svg width="18" height="18" viewBox="0 0 21 21" aria-hidden="true" style={{ flexShrink: 0 }}>
      <rect x="1"  y="1"  width="9" height="9" fill="#f25022" />
      <rect x="11" y="1"  width="9" height="9" fill="#7fba00" />
      <rect x="1"  y="11" width="9" height="9" fill="#00a4ef" />
      <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
    </svg>
  )
}

// ── Step component ────────────────────────────────────────────────────────────

function Step({ n, title, sub, last = false }: { n: number; title: string; sub: string; last?: boolean }) {
  return (
    <div style={{ display: 'flex', gap: 12, paddingBottom: last ? 0 : 16, position: 'relative' }}>
      <div style={{
        width: 24, height: 24, borderRadius: '50%',
        background: 'var(--ns-brand-soft)', color: 'var(--ns-brand-strong)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 11, fontWeight: 600, flexShrink: 0, zIndex: 1,
      }}>
        {n}
      </div>
      {!last && (
        <div style={{
          position: 'absolute', left: 11.5, top: 24, bottom: 0,
          width: 1, background: 'var(--ns-border)',
        }} />
      )}
      <div style={{ paddingTop: 2 }}>
        <div style={{ fontSize: 13, fontWeight: 500 }}>{title}</div>
        <div style={{ fontSize: 11.5, color: 'var(--ns-text-muted)', marginTop: 1 }}>{sub}</div>
      </div>
    </div>
  )
}

// ── LiveStat component ────────────────────────────────────────────────────────

function LiveStat({ value, label }: { value: string; label: string }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <div style={{
        fontFamily: "'JetBrains Mono', monospace",
        fontSize: 18, fontWeight: 600,
        color: 'oklch(1 0 0 / 0.9)',
      }}>
        {value}
      </div>
      <div style={{
        fontSize: 10, fontWeight: 600, textTransform: 'uppercase',
        letterSpacing: '0.08em', color: 'oklch(1 0 0 / 0.5)',
      }}>
        {label}
      </div>
    </div>
  )
}

// ── AnimatedDataPanel ────────────────────────────────────────────────────────

function AnimatedDataPanel() {
  const canvasRef = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    let raf = 0
    let t = 0
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches

    function draw() {
      if (!canvas || !ctx) return
      const dpr = window.devicePixelRatio || 1
      const w = canvas.width / dpr
      const h = canvas.height / dpr

      ctx.clearRect(0, 0, canvas.width, canvas.height)

      // Grid
      ctx.strokeStyle = 'oklch(1 0 0 / 0.05)'
      ctx.lineWidth = 1
      ctx.beginPath()
      for (let x = 0; x < w; x += 40) {
        ctx.moveTo(x * dpr, 0)
        ctx.lineTo(x * dpr, h * dpr)
      }
      for (let y = 0; y < h; y += 40) {
        ctx.moveTo(0, y * dpr)
        ctx.lineTo(w * dpr, y * dpr)
      }
      ctx.stroke()

      // 4 sine-wave lines
      for (let i = 0; i < 4; i++) {
        const yBase = h * (0.3 + i * 0.13)
        const amp   = 30 + i * 8
        const freq  = 0.005 + i * 0.001
        const speed = 0.015 + i * 0.005

        const grad = ctx.createLinearGradient(0, 0, w * dpr, 0)
        const edgeOpacity   = 0.08 + i * 0.04
        const centerOpacity = 0.4  + i * 0.05
        grad.addColorStop(0,    `oklch(0.85 0.07 198 / ${edgeOpacity})`)
        grad.addColorStop(0.5,  `oklch(0.85 0.07 198 / ${centerOpacity})`)
        grad.addColorStop(1,    `oklch(0.85 0.07 198 / ${edgeOpacity})`)

        ctx.beginPath()
        ctx.strokeStyle = grad
        ctx.lineWidth = 1.5

        for (let px = 0; px <= w; px += 1) {
          const y = yBase
            + Math.sin(px * freq + t * speed + i) * amp
            + Math.sin(px * freq * 2.3 + t * speed * 0.7) * (amp * 0.4)
          if (px === 0) ctx.moveTo(px * dpr, y * dpr)
          else ctx.lineTo(px * dpr, y * dpr)
        }
        ctx.stroke()
      }

      // 18 floating dots
      for (let i = 0; i < 18; i++) {
        const x     = ((i * 73 + t * 0.3) % (w + 100)) - 50
        const y     = h * (0.2 + (i * 0.13 % 0.7))
        const drift = Math.sin(t * 0.01 + i) * 12
        const size  = 1.5 + (i % 3) * 0.8
        const op    = 0.3 + (i % 3) * 0.15
        ctx.beginPath()
        ctx.fillStyle = `oklch(1 0 0 / ${op})`
        ctx.arc(x * dpr, (y + drift) * dpr, size * dpr, 0, Math.PI * 2)
        ctx.fill()
      }

      t += 1

      if (!reducedMotion) {
        raf = requestAnimationFrame(draw)
      }
    }

    function resize() {
      if (!canvas || !ctx) return
      const dpr = window.devicePixelRatio || 1
      const rect = canvas.getBoundingClientRect()
      canvas.width  = rect.width  * dpr
      canvas.height = rect.height * dpr
      ctx.scale(dpr, dpr)
      t = 0
      draw()
    }

    resize()
    window.addEventListener('resize', resize)

    return () => {
      cancelAnimationFrame(raf)
      window.removeEventListener('resize', resize)
    }
  }, [])

  return (
    <div style={{
      background: 'linear-gradient(135deg, oklch(0.32 0.04 200) 0%, oklch(0.22 0.02 230) 100%)',
      position: 'relative', overflow: 'hidden', color: 'white',
      width: '100%', height: '100%',
    }}>
      {/* Canvas */}
      <canvas
        ref={canvasRef}
        style={{
          position: 'absolute', inset: 0,
          width: '100%', height: '100%',
        }}
      />

      {/* Giant N ghost */}
      <div style={{
        position: 'absolute', right: -80, bottom: -120,
        fontSize: 480, fontWeight: 700, letterSpacing: '-0.06em',
        color: 'oklch(1 0 0 / 0.04)', lineHeight: 0.8,
        userSelect: 'none', pointerEvents: 'none',
      }}>
        N
      </div>

      {/* Top brand row */}
      <div style={{ position: 'absolute', top: 32, left: 40, display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{
          width: 32, height: 32, borderRadius: 8,
          background: 'oklch(1 0 0 / 0.12)',
          backdropFilter: 'blur(10px)',
          border: '1px solid oklch(1 0 0 / 0.2)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        }}>
          <Medallion size={22} onDark />
        </div>
        <div>
          <div style={{ fontSize: 14, fontWeight: 600, color: 'oklch(0.92 0.03 198)', letterSpacing: '-0.01em' }}>
            number<span style={{ color: 'oklch(0.88 0.07 198)' }}>skills</span>
          </div>
          <div style={{ fontSize: 9, fontWeight: 600, color: 'oklch(1 0 0 / 0.5)', textTransform: 'uppercase', letterSpacing: '0.12em' }}>
            FORGE
          </div>
        </div>
      </div>

      {/* Bottom hero copy */}
      <div style={{
        position: 'absolute', left: 40, right: 40, bottom: 110, maxWidth: 460,
      }}>
        {/* Version pill */}
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          background: 'oklch(1 0 0 / 0.1)', backdropFilter: 'blur(10px)',
          border: '1px solid oklch(1 0 0 / 0.15)',
          borderRadius: 999, padding: '4px 10px',
          fontSize: 11, color: 'oklch(1 0 0 / 0.8)',
          marginBottom: 18,
        }}>
          <div style={{ width: 6, height: 6, borderRadius: '50%', background: 'oklch(0.75 0.18 145)', flexShrink: 0 }} />
          v2026.5 · live
        </div>
        <h1 style={{
          margin: '0 0 14px',
          fontSize: 38, fontWeight: 600,
          letterSpacing: '-0.025em', lineHeight: 1.1,
          textWrap: 'balance' as React.CSSProperties['textWrap'],
          color: 'oklch(1 0 0 / 0.92)',
        }}>
          The control plane for everything Numberskills builds.
        </h1>
        <p style={{
          margin: 0,
          fontSize: 14, lineHeight: 1.55,
          color: 'oklch(1 0 0 / 0.65)',
        }}>
          Time, contracts, client portfolios and integrations — one workspace for the consulting team.
        </p>
      </div>

      {/* Live stats row */}
      <div style={{
        position: 'absolute', left: 40, right: 40, bottom: 32,
        display: 'flex', gap: 32,
      }}>
        <LiveStat value="6"      label="Active integrations" />
        <LiveStat value="2,841"  label="Records synced 24h" />
        <LiveStat value="99.98%" label="Uptime 30d" />
      </div>
    </div>
  )
}

// ── Error callout ────────────────────────────────────────────────────────────

type ErrorState = 'none' | 'auth' | 'denied'

function ErrorCallout({ error }: { error: ErrorState }) {
  if (error === 'none') return null

  if (error === 'denied') {
    return (
      <div style={{
        display: 'flex', gap: 10, padding: '10px 12px',
        background: 'var(--ns-warn-soft)', borderRadius: 8,
        marginBottom: 12, alignItems: 'flex-start',
      }}>
        <AlertTriangle size={15} style={{ color: 'var(--ns-warn)', flexShrink: 0, marginTop: 1 }} />
        <span style={{ fontSize: 12.5, color: 'var(--ns-warn)', lineHeight: 1.5 }}>
          This Microsoft account isn't part of Numberskills. Sign in with your{' '}
          <strong>@numberskills.se</strong> identity.
        </span>
      </div>
    )
  }

  return (
    <div style={{
      display: 'flex', gap: 10, padding: '10px 12px',
      background: 'var(--ns-err-soft)', borderRadius: 8,
      marginBottom: 12, alignItems: 'flex-start',
    }}>
      <AlertCircle size={15} style={{ color: 'var(--ns-err)', flexShrink: 0, marginTop: 1 }} />
      <span style={{ fontSize: 12.5, color: 'var(--ns-err)', lineHeight: 1.5 }}>
        We couldn't sign you in.{' '}
        <a href="mailto:it@numberskills.se" style={{ color: 'var(--ns-err)', textDecoration: 'underline' }}>
          Try again
        </a>{' '}
        or contact IT.
      </span>
    </div>
  )
}

// ── Shared sign-in card content ───────────────────────────────────────────────

interface SignInProps {
  signIn: () => void
  loading: boolean
  error: ErrorState
}

function SignInButton({ signIn, loading, error }: SignInProps) {
  return (
    <>
      <ErrorCallout error={error} />
      <button
        onClick={signIn}
        disabled={loading}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'flex-start',
          gap: 10, width: '100%', height: 48, borderRadius: 10,
          background: 'var(--ns-text)', color: 'var(--ns-surface)',
          border: '1px solid var(--ns-text)',
          fontSize: 14, fontWeight: 500,
          cursor: loading ? 'not-allowed' : 'pointer',
          opacity: loading ? 0.7 : 1,
          padding: '0 16px',
        }}
      >
        {loading ? (
          <>
            <RefreshCw
              size={18}
              style={{
                flexShrink: 0,
                animation: 'spin 0.8s linear infinite',
              }}
            />
            <span>Signing in…</span>
          </>
        ) : (
          <>
            <MicrosoftLogo />
            <span>Continue with Microsoft</span>
            <ArrowRight size={15} style={{ marginLeft: 'auto', opacity: 0.7 }} />
          </>
        )}
      </button>
    </>
  )
}

// ── LoginSplit (desktop ≥720px) ───────────────────────────────────────────────

function LoginSplit({ signIn, loading, error }: SignInProps) {
  return (
    <div style={{
      minHeight: '100vh', display: 'grid',
      gridTemplateColumns: '1.05fr 1fr',
      background: 'var(--ns-bg)',
    }}>
      {/* Left animated panel */}
      <AnimatedDataPanel />

      {/* Right sign-in */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '40px 56px',
      }}>
        <div style={{ width: '100%', maxWidth: 380 }}>
          <h1 style={{
            margin: '0 0 8px',
            fontSize: 24, fontWeight: 600,
            letterSpacing: '-0.018em', lineHeight: 1.2,
            color: 'var(--ns-text)',
          }}>
            Welcome back
          </h1>
          <p style={{
            margin: '0 0 28px',
            fontSize: 13.5, color: 'var(--ns-text-muted)', lineHeight: 1.55,
          }}>
            Sign in with your Numberskills Microsoft identity to continue.
          </p>

          <SignInButton signIn={signIn} loading={loading} error={error} />

          {/* Info callout */}
          <div style={{
            display: 'flex', gap: 8, marginTop: 14,
            padding: '10px 12px',
            background: 'var(--ns-surface-2)', borderRadius: 8,
            fontSize: 12, alignItems: 'flex-start',
          }}>
            <Info size={14} style={{ color: 'var(--ns-info)', flexShrink: 0, marginTop: 1 }} />
            <span style={{ color: 'var(--ns-text-muted)', lineHeight: 1.55 }}>
              Access is limited to <strong>@numberskills.se</strong> Microsoft accounts managed by the organization.
            </span>
          </div>

          {/* What happens next */}
          <div style={{ marginTop: 36 }}>
            <div style={{
              fontSize: 10.5, fontWeight: 600,
              letterSpacing: '0.1em', textTransform: 'uppercase',
              color: 'var(--ns-text-muted)', marginBottom: 14,
            }}>
              What happens next
            </div>
            <Step n={1} title="Microsoft sign-in" sub="You'll be redirected to the Microsoft login page." />
            <Step n={2} title="Identity verified" sub="Your @numberskills.se account is confirmed." />
            <Step n={3} title="Access granted" last sub="You're brought back to your Forge workspace." />
          </div>

          {/* Footer */}
          <div style={{
            display: 'flex', marginTop: 40, gap: 14,
            fontSize: 11.5, color: 'var(--ns-text-subtle)',
            justifyContent: 'space-between', alignItems: 'center',
          }}>
            <div style={{ display: 'flex', gap: 14 }}>
              <a href="mailto:it@numberskills.se" style={{ color: 'var(--ns-text-subtle)', textDecoration: 'none' }}>Help</a>
              <a href="#" style={{ color: 'var(--ns-text-subtle)', textDecoration: 'none' }}>Privacy</a>
              <a href="#" style={{ color: 'var(--ns-text-subtle)', textDecoration: 'none' }}>Status</a>
            </div>
            <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11 }}>v2026.5.1</span>
          </div>
        </div>
      </div>
    </div>
  )
}

// ── LoginMobile (mobile <720px) ───────────────────────────────────────────────

function LoginMobile({ signIn, loading, error }: SignInProps) {
  return (
    <div style={{
      minHeight: '100vh', background: 'var(--ns-surface)',
      display: 'flex', flexDirection: 'column',
    }}>
      {/* Top band */}
      <div style={{
        height: 280, position: 'relative', overflow: 'hidden',
        background: 'linear-gradient(135deg, oklch(0.32 0.04 200) 0%, oklch(0.22 0.02 230) 100%)',
        color: 'white', padding: '40px 28px 28px',
        display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
      }}>
        {/* Giant N ghost */}
        <div style={{
          position: 'absolute', right: -60, bottom: -90,
          fontSize: 320, fontWeight: 700, letterSpacing: '-0.06em',
          color: 'oklch(1 0 0 / 0.05)', lineHeight: 0.8,
          userSelect: 'none', pointerEvents: 'none',
        }}>
          N
        </div>

        {/* Static SVG wave */}
        <svg
          viewBox="0 0 360 80"
          style={{
            position: 'absolute', left: 0, right: 0, bottom: 60,
            width: '100%', height: 80, opacity: 0.4,
          }}
          aria-hidden="true"
        >
          <path
            d="M0,40 Q90,10 180,40 T360,40"
            fill="none"
            stroke="var(--ns-brand-on-dark)"
            strokeOpacity="0.5"
            strokeWidth="1.5"
          />
          <path
            d="M0,55 Q90,25 180,55 T360,55"
            fill="none"
            stroke="var(--ns-brand-on-dark)"
            strokeOpacity="0.3"
            strokeWidth="1"
          />
        </svg>

        {/* Top frosted mark */}
        <div style={{
          width: 36, height: 36, borderRadius: 9,
          background: 'oklch(1 0 0 / 0.12)',
          backdropFilter: 'blur(10px)',
          border: '1px solid oklch(1 0 0 / 0.2)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        }}>
          <Medallion size={24} onDark />
        </div>

        {/* Bottom heading */}
        <div>
          <h1 style={{
            margin: '0 0 8px',
            fontSize: 26, fontWeight: 600,
            letterSpacing: '-0.02em', lineHeight: 1.2,
            color: 'oklch(1 0 0 / 0.95)',
          }}>
            Welcome to{' '}
            <span style={{ color: 'oklch(0.92 0.03 198)' }}>number</span>
            <span style={{ color: 'oklch(0.88 0.07 198)' }}>skills</span>{' '}
            <span style={{ fontSize: 16, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'oklch(1 0 0 / 0.55)' }}>
              FORGE
            </span>
          </h1>
          <p style={{ margin: 0, fontSize: 13, color: 'oklch(1 0 0 / 0.7)', lineHeight: 1.5 }}>
            Your Numberskills operations workspace.
          </p>
        </div>
      </div>

      {/* Sign-in section */}
      <div style={{
        padding: 24, flex: 1,
        display: 'flex', flexDirection: 'column',
      }}>
        <SignInButton signIn={signIn} loading={loading} error={error} />

        {/* Lock callout */}
        <div style={{
          display: 'flex', gap: 8, marginTop: 14,
          padding: '10px 12px',
          background: 'var(--ns-surface-2)', borderRadius: 8,
          fontSize: 12, alignItems: 'flex-start',
        }}>
          <Lock size={14} style={{ color: 'var(--ns-info)', flexShrink: 0, marginTop: 1 }} />
          <span style={{ color: 'var(--ns-text-muted)', lineHeight: 1.55 }}>
            Access is limited to <strong>@numberskills.se</strong> Microsoft accounts.
          </span>
        </div>

        <div style={{ flex: 1 }} />

        {/* Footer */}
        <div style={{
          display: 'flex', gap: 14,
          fontSize: 11.5, color: 'var(--ns-text-subtle)',
          justifyContent: 'space-between', alignItems: 'center',
          paddingTop: 20,
        }}>
          <div style={{ display: 'flex', gap: 14 }}>
            <a href="mailto:it@numberskills.se" style={{ color: 'var(--ns-text-subtle)', textDecoration: 'none' }}>Help</a>
            <a href="#" style={{ color: 'var(--ns-text-subtle)', textDecoration: 'none' }}>Privacy</a>
            <a href="#" style={{ color: 'var(--ns-text-subtle)', textDecoration: 'none' }}>Status</a>
          </div>
          <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11 }}>v2026.5.1</span>
        </div>
      </div>
    </div>
  )
}

// ── Login (root) ──────────────────────────────────────────────────────────────

export default function Login() {
  const { instance, inProgress } = useMsal()
  const loading = inProgress !== 'none'

  const [error, setError] = useState<ErrorState>('none')
  const [isMobile, setIsMobile] = useState(false)

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 719px)')
    setIsMobile(mq.matches)
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches)
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [])

  const signIn = () => {
    setError('none')
    instance.loginRedirect(loginRequest).catch((err: { errorCode?: string; message?: string }) => {
      if (err?.errorCode === 'interaction_in_progress') {
        instance.clearCache()
        instance.loginRedirect(loginRequest).catch(() => setError('auth'))
        return
      }
      if (
        err?.errorCode === 'access_denied' ||
        err?.message?.includes('AADSTS50020')
      ) {
        setError('denied')
        return
      }
      setError('auth')
    })
  }

  const props: SignInProps = { signIn, loading, error }

  return (
    <>
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
      `}</style>
      {isMobile ? <LoginMobile {...props} /> : <LoginSplit {...props} />}
    </>
  )
}
